import { useReactTable, getCoreRowModel, ColumnDef, flexRender, getPaginationRowModel } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import "./candidateTables.css"
import { Row, Col } from 'react-bootstrap';

export const CandidatesTable = ({ columns, fetchData, firstName, lastName }) => {
  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const fetchDataOptions = {
    pageIndex,
    pageSize,
    firstName,
    lastName,
  };

  const defaultData = useMemo(() => [], []);
  const defaultColumn = useMemo(() => [], []);
  const dataQuery = useQuery({
    queryKey: ['data', fetchDataOptions],
    queryFn: () => fetchData(fetchDataOptions),
    placeholderData: (prev) => prev, // replaces keepPreviousData
  });

  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize]
  );

  const table = useReactTable({
    data: dataQuery.data?.rows?.data ?? defaultData,
    columns: dataQuery.data?.rows?.columns ?? defaultColumn,
    onPaginationChange: setPagination,
    pageCount: dataQuery.data?.pageCount ?? -1,
    state: {
      pagination,
    },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    debugTable: false,
    //getPaginationRowModel: getPaginationRowModel(),
  });

  const renderPageNumbers = () => {
    const totalPageCount = table.getPageCount();
    const currentPage = table.getState().pagination.pageIndex + 1;
    const pageNumbers = [];

    pageNumbers.push(1);

    if (totalPageCount > 5) {
      if (currentPage > 3) {
        pageNumbers.push('...');
      }

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPageCount - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        pageNumbers.push(i);
      }

      if (currentPage < totalPageCount - 2) {
        pageNumbers.push('...');
      }
      pageNumbers.push(totalPageCount);

    } else {
      for (let i = 2; i <= totalPageCount; i++) {
        pageNumbers.push(i);
      }
    }

    return pageNumbers;
  };

  return (
    <div className="p-2" >
      <div className='tableContainer'>
        <table className="table">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            {/* {table.getFooterGroups().map((footerGroup) => (
              <tr key={footerGroup.id}>
                {footerGroup.headers.map((header) => (
                  <th key={header.id}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.footer, header.getContext())}
                  </th>
                ))}
              </tr>
            ))} */}
          </tfoot>
        </table>
      </div>

      <div className="pagination-controls mt-3">
        <Row className="align-items-center g-2 flex-nowrap">

          <Col xs="auto">
            <div className="pagination-container d-flex gap-1">
              <button className="border rounded p-1" onClick={() => table.setPageIndex(0)} disabled={!table.getCanPreviousPage()}>{'<<'}</button>
              <button className="border rounded p-1" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>{'<'}</button>
              {renderPageNumbers().map((page, index) => (
                <button
                  key={index}
                  className={`border rounded p-1 ${pageIndex + 1 === page ? 'active' : ''}`}
                  onClick={() => typeof page === 'number' && table.setPageIndex(page - 1)}
                  disabled={typeof page !== 'number'}
                >
                  {page}
                </button>
              ))}
              <button className="border rounded p-1" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>{'>'}</button>
              <button className="border rounded p-1" onClick={() => table.setPageIndex(table.getPageCount() - 1)} disabled={!table.getCanNextPage()}>{'>>'}</button>
            </div>
          </Col>

          {/* Go To Page */}
          <Col xs="auto">
            <div className="d-flex align-items-center gap-2">
              <span style={{ fontSize: '15px' }}>Go to page:</span>
              <input
                type="number"
                defaultValue={table.getState().pagination.pageIndex + 1}
                onChange={(e) => {
                  const input = e.target.value;
                  const page = Number(input);
                  if (!input || isNaN(page)) {
                    table.setPageIndex(0);
                  } else {
                    table.setPageIndex(Math.max(0, page - 1));
                  }
                }}
                className="border p-1 rounded"
                style={{ width: '3rem' }}
              />
            </div>
          </Col>

          {/* Page Size Selector */}
          <Col xs="auto">
            <div className="d-flex align-items-center gap-2">
              <span style={{ fontSize: '15px' }}>Show</span>
              <select
                value={table.getState().pagination.pageSize}
                onChange={(e) => {
                  table.setPageSize(Number(e.target.value));
                }}
                className="border p-1 rounded"
              >
                {[10, 20, 30, 40, 50].map((size) => (
                  <option key={size} value={size} style={{ fontSize: '15px' }}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
          </Col>

        </Row>
      </div>

    </div>
  );
};